import * as React from 'react';

import { unwrapResult } from '@reduxjs/toolkit';
import { Notification } from 'farmhub-ui-core';
import { unflatten } from 'flat';
import { useDispatch } from 'react-redux';

import { useAppSelector } from 'app';
import { clearPrevRoute } from 'slice';
import {
  checkAndLoadLastPage,
  paginationInit,
  removeEmptyElement,
} from 'utils';

interface Props {
  isVisible?: boolean;
  action: any;
  query?: any;
  deps?: any;
  loadMore?: boolean;
  formatData?: (e: any) => any;
  onAfterLoad?: (values?: any) => void;
  setQuery?: any;
}

const useFetchTableData = ({
  isVisible = true,
  action,
  query = {},
  deps = [],
  setQuery,
  loadMore = false,
  formatData,
  onAfterLoad,
}: Props): any => {
  const dispatch = useDispatch();
  const [data, setData] = React.useState<any[]>([]);
  const [pagination, setPagination] = React.useState(paginationInit);
  const [isLoading, setIsLoading] = React.useState(false);
  const oldValue = React.useRef<any>(null);
  const prevRoute = useAppSelector(state => state.appRoute.prevRoute);

  const onReFreshList = async (isHasLoading?: boolean): Promise<void> => {
    const orderQuery: any = {};
    orderQuery[query.order?.name] = query.order?.value;

    try {
      if (!query) {
        return;
      }
      if (isHasLoading) {
        setIsLoading(true);
      }

      const standardQuery = unflatten({ ...query });
      const requestQuery = {
        ...standardQuery,
        order: query.order ? orderQuery : undefined,
        page: query?.page,
      };

      removeEmptyElement(requestQuery);
      const actionResult = await dispatch(action({ ...requestQuery }));
      const result = unwrapResult(actionResult);

      const { pagination: paginationResult, data: dataResult } = result;

      let formatList: any[] = [];
      if (formatData) {
        formatList = dataResult.map(item => formatData(item));
      } else {
        formatList = dataResult.map(item => {
          return {
            key: item.id,
            value: item.id,
            ...item,
          };
        });
      }

      setData(formatList);
      onAfterLoad && onAfterLoad({ formatList, result });
      setPagination(paginationResult);
    } catch (err: any) {
      if (![422, 403].includes(err?.statusCode ?? err?.code)) {
        Notification('error', 'Tải dữ liệu thất bại', err);
      }
    } finally {
      if (isHasLoading) {
        setIsLoading(false);
      }
    }
  };

  React.useEffect(() => {
    let isMounted = true;
    const orderQuery: any = {};
    orderQuery[query.order?.name] = query.order?.value;

    const standardQuery = unflatten({ ...query });
    const fetchData = async (lastPage?: number): Promise<void> => {
      setIsLoading(true);
      try {
        if (!query) {
          return;
        }
        const requestQuery = {
          ...standardQuery,
          order: orderQuery,
          page: lastPage || query?.page,
        };

        removeEmptyElement(requestQuery);

        const actionResult = await dispatch(action({ ...requestQuery }));
        const result = unwrapResult(actionResult);
        const { pagination: paginationResult, data: dataResult } = result;

        paginationResult &&
          checkAndLoadLastPage(dataResult, paginationResult, fetchData);

        if (lastPage && setQuery) {
          const newQuery = { ...query, page: lastPage || query?.page };
          setQuery(newQuery);
          oldValue.current = JSON.stringify(newQuery);
        }

        let formatList: any[] = [];
        if (formatData) {
          formatList = dataResult.map(item => formatData(item));
        } else {
          formatList = dataResult.map(item => {
            return {
              key: item.id,
              value: item.id,
              ...item,
            };
          });
        }

        if (loadMore) {
          setData((prevState: any) => [...prevState, ...formatList]);
        } else {
          setData(formatList);
        }

        onAfterLoad && onAfterLoad({ formatList, result });

        if (isMounted) {
          setPagination(paginationResult);
        }
      } catch (err: any) {
        if (![422, 403].includes(err?.statusCode ?? err?.code)) {
          Notification('error', 'Tải dữ liệu thất bại', err);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (isVisible && oldValue.current !== JSON.stringify(query)) {
      fetchData();
    }

    return () => {
      isMounted = false;
      oldValue.current = null;
      prevRoute && dispatch(clearPrevRoute());
    };
  }, [...deps, isVisible]); //eslint-disable-line

  return {
    isLoading,
    data,
    setData,
    onReFreshList,
    pagination,
    setPagination,
    query,
  };
};

export default useFetchTableData;
